import { Image, Upload, Share2, BarChart3, Settings, ChevronLeft, ChevronRight, CheckCircle, Sparkles, X, UserCircle, FolderOpen, Send, Frame } from 'lucide-react';
import { useDirectUpload } from '@/contexts/DirectUploadContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Each nav item carries its own accent glyph hue (per cockpit spec).
const navItems = [
  { id: 'photos', label: 'Photos', icon: Image, hue: '#BF212F', description: 'Browse and manage photos' },
  { id: 'upload', label: 'Upload', icon: Upload, hue: '#0FA3A3', description: 'Upload new photos' },
  { id: 'collections', label: 'Collections', icon: FolderOpen, hue: '#F2A900', description: 'Organize photos into groups' },
  { id: 'headshots', label: 'Headshots', icon: UserCircle, hue: '#7C3AED', description: 'Deliver photos by face' },
  { id: 'frames', label: 'Photo Frames', icon: Frame, hue: '#F26B38', description: 'Branded photo frame overlays' },
  { id: 'share', label: 'Share Links', icon: Share2, hue: '#2563EB', description: 'Manage share links' },
  { id: 'deliveries', label: 'Deliveries', icon: Send, hue: '#0FA3A3', description: 'Email delivery history' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, hue: '#16A34A', description: 'View statistics' },
  { id: 'settings', label: 'Settings', icon: Settings, hue: '#9A9AA0', description: 'Gallery settings' },
];

// Crew may only access these panels (upload, view/delete photos, share+email).
const CREW_PANEL_IDS = ['photos', 'upload', 'share'];

const GallerySidebar = ({
  activePanel,
  onPanelChange,
  collapsed,
  onToggleCollapse,
  gallery,
  photoCount,
  onClose,
  isMobile,
  userRole = 'admin',
}) => {
  const { uploadingCount } = useDirectUpload();
  const visibleNavItems = userRole === 'crew'
    ? navItems.filter((item) => CREW_PANEL_IDS.includes(item.id))
    : navItems;

  const isCollapsed = isMobile ? false : collapsed;

  const NavButton = ({ item }) => {
    const Icon = item.icon;
    const isActive = activePanel === item.id;
    const hasUploadNotification = item.id === 'upload' && uploadingCount > 0;
    const hasPhotosBadge = item.id === 'photos' && photoCount > 0;

    const buttonContent = (
      <button
        onClick={() => onPanelChange(item.id)}
        className="relative w-full flex items-center gap-3 transition-all"
        style={{
          borderRadius: 10,
          padding: isCollapsed ? '12px 8px' : '10px 12px',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          background: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
          color: isActive ? '#fff' : '#9A9AA0',
        }}
      >
        {/* 3px Turbo-Red left marker on active */}
        {isActive && <span style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 3, background: '#BF212F' }} />}
        <div className="relative">
          <Icon className="w-5 h-5" style={{ color: isActive ? item.hue : '#9A9AA0' }} />
          {hasUploadNotification && (
            <span className="absolute animate-pulse" style={{ top: -3, right: -3, width: 9, height: 9, background: '#16A34A', borderRadius: '50%', boxShadow: '0 0 0 2px #0A0A0B' }} />
          )}
        </div>

        {!isCollapsed && (
          <>
            <span className="font-tac-heading flex-1 text-left" style={{ fontSize: 13.5, fontWeight: 600, color: isActive ? '#fff' : '#C9C9CE' }}>
              {item.label}
            </span>
            {hasPhotosBadge && (
              <span className="font-tac-mono" style={{ fontSize: 10, fontWeight: 700, background: '#BF212F', color: '#fff', padding: '2px 7px', borderRadius: 999 }}>
                {photoCount.toLocaleString()}
              </span>
            )}
            {hasUploadNotification && (
              <span className="font-tac-mono animate-pulse" style={{ fontSize: 10, fontWeight: 700, background: '#16A34A', color: '#fff', padding: '2px 7px', borderRadius: 999 }}>
                {uploadingCount}
              </span>
            )}
          </>
        )}
      </button>
    );

    if (isCollapsed && !isMobile) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            <span>{item.label}</span>
          </TooltipContent>
        </Tooltip>
      );
    }
    return buttonContent;
  };

  return (
    <TooltipProvider>
      <aside
        className="flex flex-col transition-all duration-300 ease-in-out h-full font-tac-body"
        style={{ background: '#0A0A0B', width: isCollapsed ? 76 : 256 }}
      >
        {/* Logo / Brand */}
        <div className="flex items-center" style={{ height: 64, padding: isCollapsed ? '0 8px' : '0 16px', justifyContent: isCollapsed ? 'center' : 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {isCollapsed ? (
            <img src="/turbo-mark-white.png" alt="Turbo" style={{ height: 22, width: 'auto' }} />
          ) : (
            <img src="/turbo-logo-white.png" alt="Turbo IQ" style={{ height: 24, width: 'auto' }} />
          )}
          {isMobile && onClose && (
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#9A9AA0', cursor: 'pointer', display: 'flex' }}>
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1" style={{ padding: '16px 8px' }}>
          {visibleNavItems.map((item) => (
            <NavButton key={item.id} item={item} />
          ))}
        </nav>

        {/* Gallery Info (expanded) */}
        {!isCollapsed && gallery && (
          <div style={{ margin: '0 8px 8px', padding: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 12 }}>
            <div className="flex items-start gap-3">
              {gallery.featured_photo_thumbnail ? (
                <div className="overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 8, background: '#1a1a1c' }}>
                  <img src={gallery.featured_photo_thumbnail} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(255,255,255,0.06)' }}>
                  <Image className="w-5 h-5" style={{ color: '#9A9AA0' }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-tac-heading truncate" style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{gallery.name}</div>
                {gallery.event_date && (
                  <div className="font-tac-mono" style={{ fontSize: 10, color: '#7A7A82', marginTop: 2, letterSpacing: '0.04em' }}>
                    {new Date(gallery.event_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {gallery.finalized && (
                    <span className="font-tac-mono inline-flex items-center gap-1" style={{ fontSize: 9, fontWeight: 700, color: '#34D399', background: 'rgba(52,211,153,0.14)', padding: '2px 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      <CheckCircle className="w-3 h-3" /> Finalized
                    </span>
                  )}
                  {(gallery.ai_top_30_enabled || gallery.ai_top30_enabled) && (
                    <span className="font-tac-mono inline-flex items-center gap-1" style={{ fontSize: 9, fontWeight: 700, color: '#A78BFA', background: 'rgba(124,58,237,0.18)', padding: '2px 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      <Sparkles className="w-3 h-3" /> AI Top 30
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Collapse Toggle (desktop) */}
        <div className="hidden md:block" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: 8 }}>
          <button
            onClick={onToggleCollapse}
            className="font-tac-heading w-full flex items-center justify-center gap-2"
            style={{ color: '#9A9AA0', height: 36, borderRadius: 9, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : (<><ChevronLeft className="w-4 h-4" /> Collapse</>)}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default GallerySidebar;
